import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useSubgroup, useMyDriveObligations } from '../hooks/useSubgroups.ts';
import { Layers, AlertCircle, CheckCircle, Clock, ArrowRight, DollarSign, Calendar, Info, Download } from 'lucide-react';
import Table from '../components/Table.tsx';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import api from '../utils/axios.ts';

export default function MySubgroupPage() {
  const navigate = useNavigate();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const currentGroup = profile?.data?.current_group;
  const isIrregular = Number(currentGroup?.is_irregular) === 1;
  const member = profile?.data?.member;
  const subgroupId = member?.subgroup_id;
  const villageName = member?.village?.name;
  const subgroupName = member?.subgroup?.name;

  const { data: detailData, isLoading: isDetailLoading } = useSubgroup(subgroupId);
  const { data: obligationsData, isLoading: isObligationsLoading } = useMyDriveObligations();

  const detail = detailData?.data || null;
  const obligations = obligationsData?.data || { summary: {}, drives: [] };
  const summary = obligations.summary || {};
  const drives = obligations.drives || [];

  if (isProfileLoading || isDetailLoading || isObligationsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center space-y-2">
          <div className="mx-auto w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          <p className="text-sm text-gray-500">Loading your subgroup obligations...</p>
        </div>
      </div>
    );
  }

  if (!subgroupId || !detail) {
    return (
      <div className="p-6 max-w-xl mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 p-8 space-y-4">
        <div className="p-4 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 w-16 h-16 mx-auto flex items-center justify-center">
          <Layers size={32} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">No Category / Subgroup Assigned</h1>
        <p className="text-gray-500">
          You are currently not assigned to any group tier/subgroup. Please contact your group administrator to assign your profile.
        </p>
      </div>
    );
  }

  const getStatusBadge = (drive) => {
    switch (drive.status_key) {
      case 'settled':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CheckCircle size={14} />
            <span>Settled</span>
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400 animate-pulse">
            <AlertCircle size={14} />
            <span>{drive.status}</span>
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock size={14} />
            <span>Partially Paid</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            <Clock size={14} />
            <span>Due</span>
          </span>
        );
    }
  };

  const drivesColumns = [
    {
      label: 'Drive / Category',
      field: 'title',
      render: (row) => (
        <div>
          <span className="font-bold text-gray-900 dark:text-white block">{row.title}</span>
          {row.due_date && (
            <span className="text-xs text-gray-500 flex items-center space-x-1 mt-0.5">
              <Calendar size={12} />
              <span>Due: {new Date(row.due_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'My Target',
      field: 'target_amount',
      render: (row) => (
        <span className="font-semibold text-gray-800 dark:text-gray-200">
          KES {Number(row.target_amount || 0).toLocaleString()}
        </span>
      ),
    },
    {
      label: 'Paid to Date',
      field: 'paid_amount',
      render: (row) => (
        <div>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400 block">
            KES {Number(row.paid_amount || 0).toLocaleString()}
          </span>
          {Number(row.voluntary_paid || 0) > 0 && (
            <span className="text-[10px] font-medium text-purple-600 dark:text-purple-400 block mt-0.5">
              Req: KES {Number(row.required_paid || 0).toLocaleString()} | Vol: KES {Number(row.voluntary_paid || 0).toLocaleString()}
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'Balance Remaining',
      field: 'remaining_balance',
      render: (row) => (
        <span className={`font-bold ${row.remaining_balance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500'}`}>
          KES {Number(row.remaining_balance || 0).toLocaleString()}
        </span>
      ),
    },
    {
      label: 'Status',
      field: 'status',
      render: (row) => getStatusBadge(row),
    },
    {
      label: 'Action',
      field: 'action',
      render: (row) => (
        row.remaining_balance > 0 ? (
          <button
            onClick={() => navigate('/contributions/pay', { state: { driveId: row.drive_id, amount: row.remaining_balance } })}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-xs transition-colors flex items-center space-x-1"
          >
            <span>Pay Now</span>
            <ArrowRight size={14} />
          </button>
        ) : (
          <span className="text-xs text-gray-400 font-medium">No Balance</span>
        )
      ),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
            <Layers size={32} />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                My Category: {subgroupName || detail.subgroup.name}
              </h1>
              {villageName && (
                <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  | Village: {villageName}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2 mt-1">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
                Assigned Subgroup: {subgroupName || detail.subgroup.name}
              </span>
              {villageName && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                  Assigned Village: {villageName}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {detail.subgroup.description || `Overview of your set contribution obligations and payment statuses for category ${detail.subgroup.name}.`}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={async () => {
              try {
                const memberId = member?.id;
                const res = await api.get('/contributions/matrix-export', {
                  params: { member_id: memberId },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `my_matrix_breakdown_${(member?.name || 'member').toLowerCase().replace(/\s+/g, '_')}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Your Matrix Breakdown exported');
              } catch (error) {
                toast.error(getApiErrorMessage(error) || 'Export failed');
              }
            }}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors flex items-center space-x-2 shadow-sm"
            title="Export matrix breakdown for your individual contributions"
          >
            <Download size={16} />
            <span>Export My Matrix Breakdown</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider block">Total Drives</span>
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
              <Layers size={20} />
            </div>
          </div>
          <span className="text-3xl font-extrabold text-gray-900 dark:text-white mt-2 block">{summary.total_drives || 0}</span>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider block">Total Expected Target</span>
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
              <DollarSign size={20} />
            </div>
          </div>
          <span className="text-3xl font-extrabold text-gray-900 dark:text-white mt-2 block">
            KES {Number(summary.total_target || 0).toLocaleString()}
          </span>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider block">Total Paid to Date</span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
              <CheckCircle size={20} />
            </div>
          </div>
          <span className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-2 block">
            KES {Number(summary.total_paid || 0).toLocaleString()}
          </span>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-1 space-y-0.5">
            <span className="block text-emerald-700 dark:text-emerald-300">
              Required: KES {Number(summary.total_required_paid || summary.total_target || 0).toLocaleString()}
            </span>
            {Number(summary.total_voluntary_paid || 0) > 0 && (
              <span className="block text-purple-600 dark:text-purple-400">
                Voluntary: KES {Number(summary.total_voluntary_paid || 0).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider block">Outstanding Arrears</span>
            <div className="p-2 rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
              <AlertCircle size={20} />
            </div>
          </div>
          <span className="text-3xl font-extrabold text-rose-600 dark:text-rose-400 mt-2 block">
            KES {Number(summary.total_arrears || 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Drives Breakdown Table */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Layers size={20} className="text-blue-600" />
            <span>My Contribution Obligations & Deadlines</span>
          </h2>
          <span className="text-xs text-gray-500 font-medium">
            Targets are set according to your category ({subgroupName || detail.subgroup.name})
          </span>
        </div>

        <Table
          columns={drivesColumns}
          data={drives}
          loading={isObligationsLoading}
        />
      </div>
    </div>
  );
}

